// R9-1 — THE REPORT SHELL, LIFTED OUT OF App.tsx VERBATIM, AND THE REASON IS A DEFECT IT ALREADY HAD.
//
// This is the shell EVERY narrative reading on this site renders through: numerology, personality,
// astrocartography, the Books of Moses, the Greater Keys, petitions, herbs, spells, the Cauldron.
// One component, a dozen rooms.
//
// **R8's CLOSING CLAIM WAS THAT THE props-in/tree-out SEAM WAS SWEPT. IT WAS NOT.** That sweep was of
// the LEAF components — a card image, a title, an attachment row. The report SHELLS were never in it,
// and this one is 163 lines carrying five conditional blocks, three figure families and a step list.
// Measured rather than assumed: App.tsx still holds 41 top-level components that call no hook.
//
// **AND THE COST OF IT BEING IN THERE IS NOT HYPOTHETICAL.** W12-5's live Cauldron compile
// photographed this component printing "DATE OF BIRTH" with nothing under it — the place line had
// always been conditional and the birthday line never was, so the two families that KEEP their
// identity block (the Cauldron and petitions, W12-2's two exceptions) hit it every single time.
// Finding that cost a paid generation and a browser run, because there was no other way to see the
// tree this thing returns. Out here a gate CALLS it and reads that tree back as data, so the same
// law is arithmetic in the FAST tier: scripts/r9-report-shell.mjs.
//
// Moved VERBATIM. The three types come with it, because a type moves with the component that renders
// it — and App.tsx imports NarrativeReportData back, since eleven other views are typed by it.

import type { ReactNode } from "react";
import { reportTypeLabel } from "../reportLabels.js";
import { hidesMemberIdentity, magicBodyHeading } from "../magicRoom.js";
import { TarotCardImage } from "./TarotCardImage";
import menuLogoUrl from "../assets/menu-logo.webp";

// OV2-10: one step of a compiled working. `text` is the whole step in words (and stays the source of
// truth for the PDF and every gate); the seal fields let the reader show the seal inside the step.
export type WorkingStep = {
  text: string;
  sealName?: string;
  sealImageUrl?: string;
  sealOfGodName?: string;
  sealOfGodImageUrl?: string;
  verse?: string;
  verseRef?: string;
  activation?: boolean;
};
export type NarrativeSection = { heading: string; body: string; steps?: WorkingStep[] };
export type NarrativeReportData = {
  type: string;
  header: { name: string; dateText: string; timeText: string; place: string };
  overview: string;
  sections: NarrativeSection[];
  numbers?: Array<{ label: string; value: number | string }>;
  favorable?: string[];
  interpreted?: boolean;
  kicker?: string;
  card?: { slug: string; name: string; orientation?: string };
  // T6-1: a reading that DRAWS cards carries them, and they are shown as cards. One card readings
  // keep `card`; every multi-card reading (the bubble engine's three, a vendor spread, timing) uses
  // this, and both go through the one shared card component.
  cards?: Array<{ slug?: string; name: string; orientation?: string; position?: string }>;
  // N14-2: a report that carries a SEAL PLATE (the Books of Moses card) shows it. The url is the
  // member-only plate route; a card whose plate is not staged simply has none and says so in words.
  sealImageUrl?: string;
  sealName?: string;
  // VB: a Greater Keys pentacle is never handed over alone. When the working carries its Seal of God,
  // BOTH plates are shown, in the order they are used: the pentacle, then the name it sits inside.
  sealOfGodImageUrl?: string;
  sealOfGodName?: string;
};

export function NarrativeReportView({ data, extras, bodyHeading }: { data: NarrativeReportData; extras?: ReactNode; bodyHeading?: string }) {
  const overviewParas = (data.overview || "").split("\n").map((line) => line.trim()).filter(Boolean);
  return (
    <article className="chart-report">
      <header className="chart-report__band">
        <img src={menuLogoUrl} alt="12th House" className="chart-report__logo" />
        <p className="chart-report__kicker">{data.kicker || `${reportTypeLabel(data.type)} Report`}</p>
      </header>

      {/* W12-2, HER RULING: a Magic Room output carries THE ITEM, not the member. A spell is not a
          reading about her, and printing her name, birthday and birthplace above one says the
          working was cast from her chart, which it was not. The Cauldron and petitions keep their
          identity block — the Cauldron assembles a working FOR a person and a petition is addressed
          to a named one. src/magicRoom.js is the one law, shared with the PDF and the gate. */}
      {hidesMemberIdentity(data.type) ? null : (
        <section className="chart-report__info">
          <h3>General Information</h3>
          <div className="chart-report__info-grid">
            <p><span>Name</span>{data.header.name}</p>
            {/* W12-5, FOUND BY THE LIVE CAULDRON CAPTURE: the place line has always been conditional
                and this one never was, so the two exceptions that KEEP their identity block printed
                "DATE OF BIRTH" over nothing. The Cauldron's header is built off an empty chart on
                purpose (a working is not cast from her birth data), and a labelled empty field reads
                as a reading that failed to load rather than as a field that does not apply. */}
            {data.header.dateText ? (
              <p><span>Date of Birth</span>{data.header.dateText}</p>
            ) : null}
            {data.header.place && data.header.place !== "Unknown" ? (
              <p><span>Place of Birth</span>{data.header.place}</p>
            ) : null}
          </div>
        </section>
      )}

      {extras}

      {/* N14-2 — THE PLATE IS THE CARD. The seal the member was given, printed from her own scan,
          shown above its meaning and its verbatim conjuration. */}
      {data.sealImageUrl ? (
        <figure className="chart-report__card">
          <img className="chart-report__card-img chart-report__card-img--plate" src={data.sealImageUrl} alt={data.sealName || "The seal"} loading="lazy" />
          <figcaption>{data.sealName || "The seal"}</figcaption>
        </figure>
      ) : null}

      {/* VB — THE PAIRING IS SHOWN, NOT ONLY WRITTEN. The working already carries the placement
          instruction as its own section; this is the other half of the same law, so the member reading
          on screen sees exactly what the download gives her. */}
      {data.sealOfGodImageUrl ? (
        <figure className="chart-report__card">
          <img className="chart-report__card-img chart-report__card-img--plate" src={data.sealOfGodImageUrl} alt={data.sealOfGodName || "The Seal of God"} loading="lazy" />
          <figcaption>{data.sealOfGodName || "The Seal of God"}</figcaption>
        </figure>
      ) : null}

      {data.card ? (
        <figure className="chart-report__card">
          <TarotCardImage slug={data.card.slug} name={data.card.name} orientation={data.card.orientation} className="chart-report__card-img" />
          <figcaption>{data.card.name}{data.card.orientation === "reversed" ? " (reversed)" : ""}</figcaption>
        </figure>
      ) : null}

      {/* T6-1 — EVERY DRAWN CARD IS SHOWN. The bubble engine draws three and named them in prose
          only; the vendor spread and timing readings carried no card data at all. Same shared
          component as every other surface, so a reversed card is reversed here too. */}
      {Array.isArray(data.cards) && data.cards.length ? (
        <section className="chart-report__drawn" aria-label="The cards drawn for this reading">
          {data.cards.map((c, i) => (
            <figure className="chart-report__drawn-card" key={`${c.slug || c.name}-${i}`}>
              <TarotCardImage slug={c.slug || ""} name={c.name} orientation={c.orientation} loading="lazy" />
              <figcaption>
                {c.position ? <span className="chart-report__drawn-pos">{c.position}</span> : null}
                {c.name}{c.orientation === "reversed" ? " (reversed)" : ""}
              </figcaption>
            </figure>
          ))}
        </section>
      ) : null}

      {data.numbers && data.numbers.length ? (
        <section className="chart-report__tables">
          <div className="chart-report__table">
            <div className="chart-report__table-head">Core Numbers</div>
            <table>
              <tbody>
                {data.numbers.map((n) => (
                  <tr key={n.label}>
                    <td className="name">{n.label}</td>
                    <td className="deg">{n.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.favorable && data.favorable.length ? (
            <div className="chart-report__table">
              <div className="chart-report__table-head">Favorable Signatures</div>
              <table>
                <tbody>
                  {data.favorable.map((f, index) => (
                    <tr key={index}>
                      <td className="name">{f}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ) : null}

      <section className="chart-report__reading">
        {/* N1-1: the Cauldron hands over a SPELL, not a reading, so it passes its own heading.
            W12-2: and so does every other Magic Room family — that hand-carved single exception is
            exactly why a herb view still read "HERBS & TOOLS / Your Reading" over a paragraph about
            rue. The caller's own heading still wins; the law only supplies the default. */}
        <h3>{bodyHeading || magicBodyHeading(data.type)}</h3>
        {overviewParas.map((para, index) => (
          <p key={index} className="chart-report__overview">{para}</p>
        ))}
        <div className="chart-report__cards">
          {(data.sections || []).map((section, index) => {
            // OV2-10: a step list that carries seals renders NUMBERED, with that seal's own small
            // picture inside the step, so a newbie can see the seal they are told to stand the candle
            // on. Sections with no step cards render exactly as they always have.
            const steps = (section as { steps?: WorkingStep[] }).steps;
            // T9-2: a step list IS content, so this one cannot use the shared card — but the rule is
            // the same rule. No steps and no prose, no box.
            const hasSteps = Array.isArray(steps) && steps.length > 0;
            if (!hasSteps && !String(section.body || "").trim()) return null;
            return (
              <div key={index} className="chart-report__card chart-report__card--wide">
                <p className="chart-report__card-title">{section.heading}</p>
                {Array.isArray(steps) && steps.length ? (
                  <ol className="working-steps">
                    {steps.map((step, si) => (
                      <li key={si} className="working-steps__step">
                        <p className="working-steps__text">{step.text}</p>
                        {step.sealImageUrl ? (
                          <span className="working-steps__seals">
                            <img className="working-steps__thumb" src={step.sealImageUrl} alt={step.sealName || "Seal"} />
                            {step.sealOfGodImageUrl ? (
                              <img className="working-steps__thumb" src={step.sealOfGodImageUrl} alt={step.sealOfGodName || "Seal of God"} />
                            ) : null}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ol>
                ) : (
                  section.body.split("\n").map((line) => line.trim()).filter(Boolean).map((para, j) => (
                    <p key={j}>{para}</p>
                  ))
                )}
              </div>
            );
          })}
        </div>
      </section>
    </article>
  );
}
